import { Grid, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import FilterTable from "../../components/table/FilterTable";
import DefaultPageWrapper from "../../components/wrapper/DefaultPageWrapper";
import LoaderWrapper from "../../components/wrapper/LoaderWrapper";
import { USER_ROLES } from "../../helpers/constants";
import { useAccessControl } from "../../hooks/auth/useCheckPermission";
import usePaginationWithSearchParams from "../../hooks/usePaginationWithSearchParams";
import { useGetAgreementOverduePayments } from "../../services/agreementsService";
import TableService from "../../services/tableService";
import { formatCurrency } from "../../components/common/PriceDisplay";

import { useAgreementOverduePaymentTableConfig, AGREEMENT_OVERDUE_PAYMENT_TABLE_NAME } from "./TableConfig";;

import UniversalToolBarWithDialogs from "../../components/bar/UniversalToolBarWithDialogs";

export default function AgreementOverduePaymentPage(props) {
    const { hasAccess } = useAccessControl([USER_ROLES.ADMIN, USER_ROLES.SALES_LEGAL, USER_ROLES.SALES, USER_ROLES.SALES_MANAGER, USER_ROLES.SALES_MARKETING]);

    if (!hasAccess){
        return null
    }

    const { pageName } = props;

    const { t } = useTranslation();
    const TABLE_FIELDS_CONFIG = useAgreementOverduePaymentTableConfig();
    
    const TABLE_CONFIGS = [{ name: AGREEMENT_OVERDUE_PAYMENT_TABLE_NAME, config: TABLE_FIELDS_CONFIG }];

    const [dataLocal, setDataLocal] = useState([]);
    const [hiddenColumnsForTables, setHiddenColumnsForTables] = useState([]);
    const [columnsOrdersForTables, setColumnsOrdersForTables] = useState([]);
    const [totalOverduePayment, setTotalOverduePayment] = useState()
    const [recordsCount, setRecordsCount] = useState(0);

    const {
        resetPageNumber,
        searchParams,
        setSearchParams,
        page: dataPage,
        pageSize: dataPageSize,
        handleChangePageWithSearchParams: handleChangeDataPage,
        handleChangePageSizeWithSearchParams: handleChangeDataPageSize,
    } = usePaginationWithSearchParams();

    const { data: backendData, refetch, isLoading } = useGetAgreementOverduePayments(searchParams);
    useEffect(() => {
        if (backendData?.results) {
            setDataLocal(backendData.results);
            setRecordsCount(backendData.count);
            setTotalOverduePayment(backendData.total_overdue_payment)
        }
    }, [backendData, dataPage, dataPageSize]);

    const dataExportHeaders = useMemo(() => {
        return TableService.getTableExportHeaders(
            TABLE_FIELDS_CONFIG, // @ts-ignore
            hiddenColumnsForTables?.[AGREEMENT_OVERDUE_PAYMENT_TABLE_NAME] || [], // @ts-ignore
            columnsOrdersForTables?.[AGREEMENT_OVERDUE_PAYMENT_TABLE_NAME] || [],
        );
    }, [TABLE_FIELDS_CONFIG, hiddenColumnsForTables, columnsOrdersForTables]);

    const data = useMemo(
        () =>
            TableService.getPreparedDataForCollapsibleTable(
                TABLE_FIELDS_CONFIG,
                dataLocal, // @ts-ignore
                hiddenColumnsForTables?.[AGREEMENT_OVERDUE_PAYMENT_TABLE_NAME]
                    ? // @ts-ignore
                      hiddenColumnsForTables?.[AGREEMENT_OVERDUE_PAYMENT_TABLE_NAME]
                    : [], // @ts-ignore
                columnsOrdersForTables?.[AGREEMENT_OVERDUE_PAYMENT_TABLE_NAME]
                    ? // @ts-ignore
                      columnsOrdersForTables?.[AGREEMENT_OVERDUE_PAYMENT_TABLE_NAME]
                    : [],
            ),
        [
            TABLE_FIELDS_CONFIG,
            dataLocal,
            hiddenColumnsForTables,
            columnsOrdersForTables,
        ],
    );

    const tableComponent = () => (
        <FilterTable
            // @ts-ignore
            data={data}
            countRecords={recordsCount}
            resetPageNumber={resetPageNumber}
            style={{ maxHeight: "60vh" }}
            headersConfig={TableService.orderColumns(
                TableService.hideHiddenColumns(
                    TABLE_FIELDS_CONFIG, // @ts-ignore
                    hiddenColumnsForTables?.[AGREEMENT_OVERDUE_PAYMENT_TABLE_NAME] || [],
                ),
                // @ts-ignore
                columnsOrdersForTables?.[AGREEMENT_OVERDUE_PAYMENT_TABLE_NAME] || [],
            )}
            showCleanFilterIcon={true}
            showContextMenu={false}
            showExportToFileButton={true}
            exportToFileUrl={"agreements/agreement-overdue-payments/export/"}
            exportToFileSearchParams={searchParams}
            exportToFileHeaders={dataExportHeaders}
            exportToFileFileName={`${t("agreements.exportAgreementOverduePaymentsFileName")}.xlsx`}
            showDetailsIcon={false}
            showCheckbox={false}
            page={dataPage}
            onPageChange={handleChangeDataPage}
            rowsPerPage={dataPageSize}
            onRowsPerPageChange={handleChangeDataPageSize}
            setDataLocal={setDataLocal}
            isLoading={isLoading}
        />
    );

    return (
        // @ts-ignore
        <DefaultPageWrapper tagTitle={t("headers.agreement_overdue_payments")}>
            <Grid
                item
                container
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                rowSpacing={1}
                columnSpacing={2}
            >
                <Grid item xs={12} marginY={1}>
                    <UniversalToolBarWithDialogs
                        pageName={pageName}
                        tableConfig={TABLE_CONFIGS}
                        refreshTable={refetch}
                        resetPageNumber={resetPageNumber}
                        hiddenColumnsForTables={hiddenColumnsForTables}
                        setHiddenColumnsForTables={setHiddenColumnsForTables}
                        columnsOrdersForTables={columnsOrdersForTables}
                        setColumnsOrdersForTables={setColumnsOrdersForTables}
                        showMassActionButton={false}
                        showOpenCloseFilterButton={false}
                        showCleanfiltersButton={false}
                        showMyFiltersButton={false}
                        showSearchButton={false}
                        style={{ marginBlock: "5px" }}
                    />
                </Grid>
                <Grid item xs={12}>
                    <LoaderWrapper showLoader={false}>
                        {tableComponent()}
                        {totalOverduePayment !== undefined && <Typography
                            color={"red"}
                            style={{
                                position: "absolute",
                                marginTop: "-40px",
                                marginLeft: "700px",
                                fontWeight: "bold",
                                fontSize: "18px",
                            }}
                        >
                            {t("agreements.totalOverduePayment")}
                            {": "}
                            {formatCurrency(totalOverduePayment)}
                        </Typography>}
                    </LoaderWrapper>
                </Grid>
            </Grid>
        </DefaultPageWrapper>
    );
}